import {
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type LiveStreamMessage,
  type NotificationItem,
} from '@itc/shared';
import { defineStore } from 'pinia';
import { ref, shallowRef } from 'vue';
import { api } from './api.ts';
import type { LiveClient } from './live/client.ts';

// The user's notifications in the browser (design §6.7, plan 1C Chunk 6): the
// unread count and the latest few for the top bar's panel, the inbox page's list,
// and the toast queue. New items arrive on the live stream; a resync loads again.

/** How many toasts show at once; the rest wait their turn. */
export const MAX_TOASTS = 3;
/** How long a toast stays. */
export const TOAST_MS = 8_000;
/** How many items the top bar's panel shows. */
export const RECENT_COUNT = 10;
const PAGE_SIZE = 30;

export interface ToastEntry {
  id: string;
  item: NotificationItem;
}

export type InboxFilter = 'all' | 'unread';

export const useNotificationsStore = defineStore('itc-notifications', () => {
  const unreadCount = ref(0);
  /** The latest items, for the panel. */
  const recent = shallowRef<NotificationItem[]>([]);
  /** The inbox page's list. */
  const filter = ref<InboxFilter>('all');
  const items = shallowRef<NotificationItem[]>([]);
  const nextCursor = ref<string | null>(null);
  const loading = ref(false);
  const toasts = shallowRef<ToastEntry[]>([]);
  const waiting: ToastEntry[] = [];
  const timers = new Map<string, ReturnType<typeof setTimeout>>();
  let unbind: (() => void) | undefined;
  let generation = 0;
  /** Whether the inbox page has loaded its list, so a resync reloads it too. */
  let inboxLoaded = false;

  /** Loads the unread count and the panel's items. */
  async function load(): Promise<void> {
    const page = await api(listNotifications, { query: { limit: RECENT_COUNT } });
    recent.value = page.items;
    unreadCount.value = page.unreadCount;
  }

  /** Loads the inbox's first page for a filter. */
  async function loadInbox(next: InboxFilter = filter.value): Promise<void> {
    const mine = ++generation;
    filter.value = next;
    inboxLoaded = true;
    loading.value = true;
    try {
      const page = await api(listNotifications, { query: { limit: PAGE_SIZE, ...(next === 'unread' ? { unread: 'true' as const } : {}) } });
      if (mine !== generation) return;
      items.value = page.items;
      nextCursor.value = page.nextCursor;
      unreadCount.value = page.unreadCount;
    } finally {
      if (mine === generation) loading.value = false;
    }
  }

  /** Loads the inbox's next page, for infinite scroll. */
  async function loadMore(): Promise<void> {
    if (nextCursor.value === null || loading.value) return;
    const mine = generation;
    loading.value = true;
    try {
      const page = await api(listNotifications, { query: { limit: PAGE_SIZE, cursor: nextCursor.value, ...(filter.value === 'unread' ? { unread: 'true' as const } : {}) } });
      if (mine !== generation) return;
      const seen = new Set(items.value.map((item) => item.id));
      items.value = [...items.value, ...page.items.filter((item) => !seen.has(item.id))];
      nextCursor.value = page.nextCursor;
    } finally {
      if (mine === generation) loading.value = false;
    }
  }

  function replace(updated: NotificationItem): void {
    const swap = (list: NotificationItem[]) => list.map((item) => (item.id === updated.id ? updated : item));
    recent.value = swap(recent.value);
    // An unread-only list drops what was read.
    items.value = filter.value === 'unread' && updated.readAt !== null ? items.value.filter((item) => item.id !== updated.id) : swap(items.value);
  }

  async function markRead(id: string): Promise<void> {
    const answer = await api(markNotificationRead, { params: { id } });
    replace(answer.item);
    unreadCount.value = answer.unreadCount;
  }

  async function markAllRead(): Promise<void> {
    const answer = await api(markAllNotificationsRead);
    unreadCount.value = answer.unreadCount;
    const now = new Date().toISOString();
    const read = (list: NotificationItem[]) => list.map((item) => (item.readAt === null ? { ...item, readAt: now } : item));
    recent.value = read(recent.value);
    items.value = filter.value === 'unread' ? [] : read(items.value);
  }

  /** A notification from the live stream. */
  function receive(message: Extract<LiveStreamMessage, { type: 'notification' }>): void {
    const item = message.item as NotificationItem;
    unreadCount.value = message.unreadCount;
    if (!recent.value.some((existing) => existing.id === item.id)) recent.value = [item, ...recent.value].slice(0, RECENT_COUNT);
    if (inboxLoaded && !items.value.some((existing) => existing.id === item.id)) items.value = [item, ...items.value];
    if (!item.suppressed) toast(item);
  }

  function toast(item: NotificationItem): void {
    const entry = { id: item.id, item };
    if (toasts.value.length >= MAX_TOASTS) {
      waiting.push(entry);
      return;
    }
    show(entry);
  }

  function show(entry: ToastEntry): void {
    toasts.value = [...toasts.value, entry];
    timers.set(
      entry.id,
      setTimeout(() => {
        dismiss(entry.id);
      }, TOAST_MS),
    );
  }

  function dismiss(id: string): void {
    clearTimeout(timers.get(id));
    timers.delete(id);
    toasts.value = toasts.value.filter((entry) => entry.id !== id);
    const next = waiting.shift();
    if (next !== undefined) show(next);
  }

  /** Follows a live client's notifications, and loads again after a resync. */
  function bind(client: LiveClient): void {
    unbind?.();
    const offs = [
      client.onNotification(receive),
      client.onResync(() => {
        void load();
        if (inboxLoaded) void loadInbox();
      }),
    ];
    unbind = () => {
      for (const off of offs) off();
    };
  }

  /** Forgets everything, on logout. */
  function reset(): void {
    for (const timer of timers.values()) clearTimeout(timer);
    timers.clear();
    waiting.length = 0;
    toasts.value = [];
    recent.value = [];
    items.value = [];
    nextCursor.value = null;
    unreadCount.value = 0;
    inboxLoaded = false;
  }

  return { unreadCount, recent, filter, items, nextCursor, loading, toasts, load, loadInbox, loadMore, markRead, markAllRead, receive, dismiss, bind, reset };
});
